import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, X } from 'lucide-react';
import type { ProposalOpsReviewComment } from '../../types';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import ProposalStructureMentionText from './ProposalStructureMentionText';
import StructureMentionTextarea from './StructureMentionTextarea';
import type { StructureMentionProjectOption } from '../../utils/structureMentionProjectOptions';
import './ProposalOpsReview.css';

interface OpsReviewStructureCommentsPopoutProps {
  comments: ProposalOpsReviewComment[];
  canComment: boolean;
  onAddComment: (message: string) => Promise<void>;
  /** Shown in popout header (e.g. Design, Timbers). */
  sectionLabel?: string;
  className?: string;
  defaultDealId?: string;
  defaultStructures?: StructureRef[];
  projectOptions?: StructureMentionProjectOption[];
  enableProjectPicker?: boolean;
}

const OpsReviewStructureCommentsPopout: React.FC<OpsReviewStructureCommentsPopoutProps> = ({
  comments,
  canComment,
  onAddComment,
  sectionLabel,
  className = '',
  defaultDealId = '',
  defaultStructures = [],
  projectOptions = [],
  enableProjectPicker = false,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draft.trim() || submitting || !canComment) return;
    setSubmitting(true);
    try {
      await onAddComment(draft.trim());
      setDraft('');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (d: Date) =>
    d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  const title = sectionLabel ? `OPS Review · ${sectionLabel}` : 'OPS Review';

  return (
    <>
      <button
        type="button"
        className={`por-structure-comments-btn ${open ? 'active' : ''} ${comments.length > 0 ? 'has-comments' : ''} ${className}`.trim()}
        onClick={handleOpen}
        title={
          comments.length > 0
            ? `${comments.length} OPS review comment(s) — ${sectionLabel || 'section'}`
            : `OPS review comments — ${sectionLabel || 'section'}`
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`OPS review comments for ${sectionLabel || 'section'}`}
      >
        <MessageSquare size={13} strokeWidth={1.75} />
        {comments.length > 0 && <span className="por-structure-comments-count">{comments.length}</span>}
      </button>

      {open &&
        createPortal(
          <div className="por-overlay" onClick={close} role="presentation">
            <div
              className="por-modal por-section-comments-modal"
              role="dialog"
              aria-label={title}
              onClick={e => e.stopPropagation()}
            >
              <div className="por-header">
                <h2>{title}</h2>
                <button type="button" className="por-close" onClick={close} aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <div className="por-body">
                {comments.length === 0 ? (
                  <div className="por-empty">
                    {canComment ? 'No comments yet. Add one below.' : 'No comments on this section yet.'}
                  </div>
                ) : (
                  <div className="por-section-comments-list">
                    {comments.map(c => (
                      <div key={c.id} className="por-comment-item">
                        <div className="por-comment-meta">
                          {c.authorName} · {formatTime(c.createdAt)}
                        </div>
                        <div className="por-comment-text">
                          <ProposalStructureMentionText
                            message={c.message}
                            defaultDealId={defaultDealId}
                            defaultStructures={defaultStructures}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {canComment && (
                  <form className="por-section-comments-form" onSubmit={e => void handleSubmit(e)}>
                    <StructureMentionTextarea
                      value={draft}
                      onChange={setDraft}
                      defaultDealId={defaultDealId}
                      defaultStructures={defaultStructures}
                      projectOptions={projectOptions}
                      enableProjectPicker={enableProjectPicker}
                      className="por-textarea"
                      placeholder="Leave a comment… (/ to tag a structure)"
                      rows={4}
                      autoFocus
                    />
                    <div className="por-footer" style={{ borderTop: 'none', padding: '12px 0 0' }}>
                      <button type="button" className="por-btn por-btn-secondary" onClick={close}>
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="por-btn por-btn-primary"
                        disabled={!draft.trim() || submitting}
                      >
                        {submitting ? 'Saving…' : 'Add comment'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default OpsReviewStructureCommentsPopout;
